import React, { useEffect, useRef, useState } from 'react';

import { QueryBuilder, RuleGroupType, ValueEditorProps, defaultControlElements, ActionProps } from 'react-querybuilder';
import 'react-querybuilder/dist/query-builder.css';
import './query-filter.scss';

import { apiGet } from '../../api';
import AddGroupAction from './AddGroupAction';
import FilterSelector from './FilterSelector';
import { getInitialFilter, clearActiveFilter, setActiveFilter, StoredFilter } from './filterStorage';
import CombinatorSelector from './CombinatorSelector';
import FieldSelector from './FieldSelector';
import OperatorSelector from './OperatorSelector';
import { translations } from './locales/ru';
import { resolveFieldPath } from './utils';
import { FieldData, Suggestion } from './enums';
import { LoadingOverlay } from './LoadingOverlay';
import { ValueEditor } from './valueEditor';
import AddRuleAction from "./AddRuleAction";

const TrashIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
    </svg>
);

const CopyIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
        <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
    </svg>
);

export const DEFAULT_QUERY: RuleGroupType = { combinator: "and", rules: [] };
const FILTER_VISIBILITY_KEY = 'query_filter_visibility';

const CACHE_TTL = 10 * 60 * 1000; // 10 minutes

const MAX_LEVEL_GROUPS = 3;
const MAX_LIMIT_RULES = 12;

function getInitialFieldsFromCache(path: string) {
    const cacheKey = `query_initial_fields_cache:${path}`;
    const cached = localStorage.getItem(cacheKey);

    if (cached) {
        try {
            const parsed = JSON.parse(cached);
            if (Date.now() - parsed.timestamp < CACHE_TTL) {
                return parsed;
            }
        } catch (e) {
            console.warn("Invalid cache format", e);
        }
    }

    return null;
}

function setInitialFieldsCache(path: string, data: { fields: FieldData; content_type: number }) {
    const cacheKey = `query_initial_fields_cache:${path}`;
    const cacheData = {
        timestamp: Date.now(),
        fields: data.fields,
        content_type: data.content_type
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
}

function getSavedQuery(contentType: number): RuleGroupType | null {
    const key = `query_filter_rules_${contentType}`;
    const saved = localStorage.getItem(key);
    if (saved) {
        try {
            return JSON.parse(saved);
        } catch {
            return null;
        }
    }
    return null;
}

function saveQueryToStorage(contentType: number, query: RuleGroupType) {
    const key = `query_filter_rules_${contentType}`;
    localStorage.setItem(key, JSON.stringify(query));
}


const customControls: Partial<any> = {
    ...defaultControlElements,
    addRuleAction: (props: ActionProps) => {
        if (props.context.countRules >= MAX_LIMIT_RULES) {
            return null;
        }
        return (
            <AddRuleAction props={props} />
        )
    },
    addGroupAction: (props: ActionProps) => {
        if (props.level >= MAX_LEVEL_GROUPS) {
            return null;
        }
        return (
            <AddGroupAction props={props} />
        )
    },
    cloneRuleAction: (props: ActionProps) => (
        <button
            type="button"
            className="rd-btn rd-btn--secondary rd-btn--medium"
            onClick={props.handleOnClick}
            title={props.title}
        >
            {props.label}
        </button>
    ),
    removeRuleAction: (props: ActionProps) => (
        <button
            type="button"
            className="qf-icon-btn qf-icon-btn--error"
            onClick={props.handleOnClick}
            title={props.title}
        >
            <TrashIcon />
        </button>
    ),
    cloneGroupAction: (props: ActionProps) => {
        // Hide clone for root group
        if (props.level === 0) return null;
        return (
            <button
                type="button"
                className="qf-icon-btn qf-icon-btn--default"
                onClick={props.handleOnClick}
                title={props.title}
            >
                <CopyIcon />
            </button>
        );
    },
    removeGroupAction: (props: ActionProps) => {
        // Hide remove for root group
        if (props.level === 0) return null;
        return (
            <button
                type="button"
                className="qf-icon-btn qf-icon-btn--error"
                onClick={props.handleOnClick}
                title={props.title}
            >
                <TrashIcon />
            </button>
        );
    },
    combinatorSelector: CombinatorSelector,
    fieldSelector: FieldSelector,
    operatorSelector: OperatorSelector,
    valueEditor: (props: ValueEditorProps) => {
        // @ts-ignore
        return <ValueEditor {...props} fieldData={resolveFieldPath(props.schema.fields, props.field)?.at(-1)} />;
    },
};


function getCountRules(group: RuleGroupType): number {
    return group.rules.reduce((count, rule) => {
        if ('rules' in rule) {
            return count + getCountRules(rule as RuleGroupType);
        } else {
            return count + 1;
        }
    }, 0);
}

interface QueryFilterProps {
    onApply: (rules: RuleGroupType, quickSearch: string) => void;
    isLoading?: boolean;
}

export const QueryFilter = ({ onApply, isLoading }: QueryFilterProps) => {
    const cached_key = window.location.pathname;
    const cached = getInitialFieldsFromCache(cached_key);

    const [initFields, setInitFields] = useState(cached ? cached.fields : [])
    const [contentType, setContentType] = useState(cached ? cached.content_type : 0);
    const [countRules, setCountRules] = useState(0);
    const [activeFilterName, setActiveFilterName] = useState<string | null>(null);

    const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
        const saved = localStorage.getItem(FILTER_VISIBILITY_KEY);
        return saved !== null ? !JSON.parse(saved) : true;
    });
    const [isDisabled, setIsDisabled] = useState<boolean>(false);

    // Cache for suggestions
    const suggestionsCache = useRef(new Map<string, {
        suggestions: Suggestion[];
        lastPage: number;
        hasMorePages: boolean;
    }>());

    const [query, setQuery] = useState<RuleGroupType>(() => {
        return DEFAULT_QUERY
    });

    // Load saved query from localStorage when contentType is known
    useEffect(() => {
        if (contentType === 0) return;
        const initial = getInitialFilter(contentType);
        if (initial) {
            setQuery(initial.rules);
            setActiveFilterName(initial.name);
            return;
        }
        const saved = getSavedQuery(contentType);
        if (saved && saved.rules?.length > 0) {
            setQuery(saved);
        }
    }, [contentType]);

    useEffect(() => {
        if (!cached) {
            apiGet("table_filter_fields/").then((data: any) => {
                setInitFields(data.fields);
                setContentType(data.content_type);
                setInitialFieldsCache(cached_key, data);
            }).catch(() => {
                setInitFields([]);
                setQuery(DEFAULT_QUERY)
                setIsDisabled(true)
            });
        }
    }, [cached, cached_key]);

    // Persist query to localStorage on change
    useEffect(() => {
        if (contentType > 0) {
            saveQueryToStorage(contentType, query);
        }
        setCountRules(getCountRules(query));
    }, [query, contentType]);

    function handleShowRules(show?: boolean) {
        setIsCollapsed(prev => {
            const next = typeof show === 'boolean' ? !show : !prev;
            localStorage.setItem(FILTER_VISIBILITY_KEY, JSON.stringify(!next));
            return next;
        });
    }
    function handleResetActiveFilter() {
        setActiveFilterName(null);
        if (contentType > 0) {
            clearActiveFilter(contentType);
        }
    }
    function handleSelectActiveFilter(filter: StoredFilter) {
        setActiveFilterName(filter.name);
        if (contentType > 0) {
            setActiveFilter(contentType, filter);
        }
    }

    const fieldsReady = initFields.length > 0;

    return (
        <div className={`qf${isCollapsed ? ' qf--collapsed' : ''}`}>
            {isLoading && <LoadingOverlay />}
            <div className="qf__heading">
                Фильтр
                {contentType > 0 && (
                    <FilterSelector
                        contentType={contentType}
                        defaultQuery={DEFAULT_QUERY}
                        activeFilter={activeFilterName}
                        onActiveFilterChange={setActiveFilterName}
                        onSelect={(rules, _label) => {
                            setQuery(rules);
                            onApply(rules, "");
                        }}
                    />
                )}
            </div>
                <QueryBuilder
                    controlClassnames={{
                        queryBuilder: 'qf-query',
                    }}
                    fields={initFields}
                    context={
                        {
                            "setQuery": setQuery,
                            "query": query,
                            "contentType": contentType,
                            "defaultQuery": DEFAULT_QUERY,
                            "isCollapsed": isCollapsed,
                            "handleShowRules": handleShowRules,
                            "countRules": countRules,
                            "suggestionsCache": suggestionsCache,
                            "onApply": onApply,
                            "isLoading": isLoading,
                            "fieldsReady": fieldsReady,
                            "onResetActiveFilter": handleResetActiveFilter,
                            "onSelectActiveFilter": handleSelectActiveFilter,
                        }
                    }
                    query={query}
                    onQueryChange={(query) => {
                        setQuery(query);
                    }}
                    showCloneButtons
                    disabled={isDisabled}
                    showCombinatorsBetweenRules
                    parseNumbers={true}
                    showNotToggle
                    controlElements={customControls}
                    autoSelectOperator={true}
                    resetOnOperatorChange={true}
                    showShiftActions
                    getDefaultOperator={(field: string) => {
                        // @ts-ignore
                        return resolveFieldPath(initFields, field)?.at(-1)?.predicates[0] || "exact";
                    }}
                    translations={translations}
                    combinators={
                        [
                            { name: 'AND', label: 'И' },
                            { name: 'OR', label: 'ИЛИ' },
                        ]
                    }
                    accessibleDescriptionGenerator={(props: any) => {
                        return ""
                    }}
                />
        </div>
    );
};
